import React from 'react';
import { Star, MoreHorizontal, Trash2, User, Clock } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useWorkspace } from '../contexts/WorkspaceContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const PRIORITY_STYLES = {
  low: 'badge-blue',
  medium: 'badge-yellow',
  high: 'badge-pink',
};

const PRIORITY_LABELS = {
  low: 'Låg',
  medium: 'Medium',
  high: 'Hög',
};

const TAG_STYLES = {
  'WeBreath': 'badge-emerald',
  'Enduroskola': 'badge-blue',
  'Business': 'badge-yellow',
  'Personal': 'badge-pink',
  default: 'badge-blue',
};

// Format relative time in Swedish
function formatRelativeTime(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just nu';
  if (diffMins < 60) return `${diffMins} min sedan`;
  if (diffHours < 24) return `${diffHours} tim sedan`;
  if (diffDays === 1) return 'Igår';
  if (diffDays < 7) return `${diffDays} dagar sedan`;
  
  return date.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' });
}

export function TaskCard({ task, overlay = false, onEdit }) {
  const { updateTask, deleteTask } = useWorkspace();
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleToggleStar = async (e) => {
    e.stopPropagation();
    await updateTask(task.id, { is_starred: !task.is_starred });
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    await deleteTask(task.id);
  };

  const handleToggleFocus = async (e) => {
    e.stopPropagation();
    await updateTask(task.id, { is_today_focus: !task.is_today_focus });
  };

  const handleClick = (e) => {
    // Don't trigger if clicking on interactive elements
    if (e.target.closest('button') || e.target.closest('[role="menuitem"]')) {
      return;
    }
    if (onEdit) {
      onEdit(task);
    }
  };

  if (overlay) {
    return (
      <div className="p-4 bg-[#1C1C1E] border border-white/10 rounded-lg rotate-3 shadow-2xl">
        <TaskCardContent task={task} onToggleStar={handleToggleStar} />
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={handleClick}
      className={`p-4 bg-[#1C1C1E] border border-white/10 rounded-lg cursor-grab active:cursor-grabbing hover:border-white/20 transition-all duration-200 group ${
        isDragging ? 'rotate-3 shadow-2xl' : ''
      }`}
      data-testid={`task-card-${task.id}`}
    >
      <TaskCardContent 
        task={task} 
        onToggleStar={handleToggleStar} 
        onDelete={handleDelete}
        onToggleFocus={handleToggleFocus}
      />
    </div>
  );
}

function TaskCardContent({ task, onToggleStar, onDelete, onToggleFocus }) {
  const tagStyle = TAG_STYLES[task.tag] || TAG_STYLES.default;

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-medium text-zinc-100 flex-1 line-clamp-2">
          {task.title}
        </h3>
        <div className="flex items-center gap-1">
          {/* Star button - always visible if starred, otherwise on hover */}
          <button
            onClick={onToggleStar}
            className={`p-1 rounded hover:bg-white/10 transition-all ${
              task.is_starred 
                ? 'text-yellow-400 opacity-100' 
                : 'text-zinc-500 opacity-0 group-hover:opacity-100'
            }`}
            data-testid={`star-task-${task.id}`}
          >
            <Star className="w-4 h-4" fill={task.is_starred ? 'currentColor' : 'none'} />
          </button>
          
          {/* Menu button - only on hover */}
          {onDelete && (
            <div className="opacity-0 group-hover:opacity-100 transition-opacity">
              <DropdownMenu>
              <DropdownMenuTrigger className="p-1 rounded hover:bg-white/10 transition-colors text-zinc-500" data-testid={`task-menu-${task.id}`}>
                <MoreHorizontal className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#141416] border-white/10">
                <DropdownMenuItem 
                  onClick={onToggleFocus}
                  className="cursor-pointer focus:bg-white/5"
                >
                  <Star className="w-4 h-4 mr-2" />
                  {task.is_today_focus ? 'Ta bort från fokus' : 'Lägg till i fokus'}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={onDelete}
                  className="cursor-pointer focus:bg-white/5 text-red-400 focus:text-red-400"
                  data-testid={`delete-task-${task.id}`}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Ta bort
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          )}
        </div>
      </div>

      {task.description && (
        <p className="text-xs text-zinc-500 line-clamp-2">{task.description}</p>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {task.tag && (
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${tagStyle}`}>
            {task.tag}
          </span>
        )}
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${PRIORITY_STYLES[task.priority]}`}>
          {PRIORITY_LABELS[task.priority]}
        </span>
        {task.is_today_focus && (
          <span className="px-2 py-0.5 rounded text-xs font-medium badge-emerald">
            Fokus
          </span>
        )}
      </div>

      {/* Creator name and created time */}
      <div className="flex items-center gap-3 text-xs text-zinc-600 pt-1">
        {task.created_by_name && (
          <div className="flex items-center gap-1">
            <User className="w-3 h-3" />
            <span>{task.created_by_name}</span>
          </div>
        )}
        {task.created_at && (
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            <span>{formatRelativeTime(task.created_at)}</span>
          </div>
        )}
      </div>
    </div>
  );
}
